import axios from "axios";

// Resolve the API base. In dev (`yarn start`) we always honour REACT_APP_BACKEND_URL
// so localhost can talk to the preview backend. In production, the value baked in
// at build time may point at a *different* host than the page itself (e.g. the app
// is now served on a custom domain like the1365.com instead of the preview URL).
// In that case, fall back to a same-origin path — the Kubernetes ingress routes
// every request prefixed with /api to the backend regardless of domain.
function resolveApiBase() {
  const env = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (typeof window === "undefined") return env;
  if (!env) return ""; // already same-origin
  try {
    const envHost = new URL(env).host;
    const pageHost = window.location.host;
    if (envHost === pageHost) return env;
    // Host mismatch — only override in real deployments (not localhost dev).
    if (/^localhost(:\d+)?$/i.test(pageHost) || /^127\.0\.0\.1(:\d+)?$/.test(pageHost)) {
      return env;
    }
    return ""; // same-origin
  } catch (e) {
    return env;
  }
}

const BASE = resolveApiBase();
export const API = `${BASE}/api`;

export const http = axios.create({ baseURL: API });

http.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("sa_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // session expired
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register") {
        localStorage.removeItem("sa_token");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(e) {
  // Network-level failures (no response received) — never show raw axios text.
  if (e && !e.response) {
    if (e.code === "ECONNABORTED") return "That took too long. Please check your connection and try again.";
    if (e.message === "Network Error") return "Can't reach Vidhya right now. Check your connection and try again.";
    return "We hit a snag connecting. Please try again in a moment.";
  }
  const status = e?.response?.status;
  // Server-side outage / proxy errors — translate to friendly copy.
  if (status === 502 || status === 503 || status === 504) {
    return "Vidhya is briefly unavailable. Please try again in a moment.";
  }
  if (status === 429) return "You're going a bit fast — give it a few seconds and try again.";
  if (status === 500) return "Something went wrong on our end. Please try again — if it persists, ping support.";
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  if (d?.message) return d.message;
  if (d?.msg) return d.msg;
  if (d?.error) return d.error;
  // Object detail with no message field — never let `[object Object]` reach the UI.
  try { return JSON.stringify(d); } catch { return "Something went wrong."; }
}
